import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import relativeTime from 'dayjs/plugin/relativeTime';
import { TaskStatusType } from '@/app/enums/TaskStatusType';
import { type TaskInfoDto } from '@/app/models/TaskInfoDto';
import { useDismissTask, useDownloadTaskFile } from '@/app/modules/task/core/_hooks';
import { isAutomationTask, isRunning } from '@/app/modules/task/core/_utils';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const STATUS_ICON: Record<TaskStatusType, string> = {
  [TaskStatusType.RUNNING]: 'spinner-border spinner-border-sm text-primary',
  [TaskStatusType.COMPLETED]: 'bi bi-check-circle-fill fs-4 text-success',
  [TaskStatusType.FAILED]: 'bi bi-x-circle-fill fs-4 text-danger',
  [TaskStatusType.CANCELLED]: 'bi bi-x-circle-fill fs-4 text-danger',
};

type Props = { task: TaskInfoDto<string> };

/** Linha da aba Tarefas. O texto trunca dentro do menu; a ação fica sempre na linha. */
const TaskRow = ({ task }: Props) => {
  const download = useDownloadTaskFile();
  const dismiss = useDismissTask();

  const title = task.title ?? task.name;
  const percent = Math.round((task.progress ?? 0) * 100);
  const detail = [task.detail, task.start_time && dayjs(task.start_time).fromNow()].filter(Boolean).join(' · ');
  const running = isRunning(task);
  const isCompleted = task.status === TaskStatusType.COMPLETED;
  const hasDownload = isCompleted && !isAutomationTask(task.type);
  /** `FAILED` ou `CANCELLED`: terminou sem arquivo. */
  const endedWithoutFile = !running && !isCompleted;

  return (
    <div
      className="d-flex align-items-center gap-3 px-6 py-3 animate__animated animate__fadeIn animate__faster"
      role="listitem"
    >
      <span className={`flex-shrink-0 ${STATUS_ICON[task.status]}`} />
      <div className="flex-grow-1 overflow-hidden">
        <div className="fw-semibold text-gray-900 fs-7 text-truncate">{title}</div>
        <div className="fs-8 text-gray-600 text-truncate">{detail}</div>
        {running ? (
          <>
            <div
              aria-label={`Progresso de ${title}`}
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuenow={percent}
              className="progress bg-light-primary h-4px mt-2"
              role="progressbar"
            >
              <div className="progress-bar bg-primary rounded" style={{ width: `${percent}%` }} />
            </div>
            {task.stage ? <div className="fs-8 text-primary text-truncate mt-1">{task.stage}</div> : null}
          </>
        ) : null}
        {endedWithoutFile && task.error ? <div className="fs-8 text-danger mt-1">{task.error}</div> : null}
      </div>
      {hasDownload ? (
        <button
          className="btn btn-sm btn-light-success text-nowrap flex-shrink-0"
          disabled={download.isPending}
          type="button"
          onClick={() => download.mutate(task)}
        >
          Baixar
        </button>
      ) : null}
      {/* Concluída sai pelo "Limpar finalizadas"; a que falhou o usuário lê o motivo e remove. */}
      {endedWithoutFile ? (
        <button
          aria-label={`Remover ${title}`}
          className="btn btn-sm btn-icon btn-active-light w-25px h-25px flex-shrink-0"
          disabled={dismiss.isPending}
          type="button"
          onClick={() => dismiss.mutate(task)}
        >
          <i className="bi bi-x fs-3 p-0" />
        </button>
      ) : null}
    </div>
  );
};

export default TaskRow;
